import { useEffect, useState, useCallback } from "react";
import {
  Users, DollarSign, TrendingUp, ShieldCheck, Activity, Ban, FlaskConical, BadgeCheck,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { KPI, PLAN_COLORS } from "@/components/admin/adminShared";

export default function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [health, setHealth] = useState(null);
  const [mode, setMode] = useState("real"); // 'real' | 'test'

  const load = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/revenue").then(({ data }) => setRevenue(data)).catch(() => {});
    api.get("/admin/health").then(({ data }) => setHealth(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const planData = stats ? Object.entries(stats.plan_breakdown).map(([k, v]) => ({ name: k, value: v })) : [];
  const dr = stats?.data_reality;
  const b = dr ? (mode === "real" ? dr.real : dr.synthetic) : null;
  const isReal = mode === "real";

  return (
    <div className="space-y-6">
      {/* Data-reality toggle: genuine traction vs seeded/test activity — no number is ever conflated. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-slate-200 bg-white rounded-lg p-3">
        <div className="flex items-center gap-2 text-sm">
          {isReal ? <BadgeCheck size={16} className="text-[#16A34A]" /> : <FlaskConical size={16} className="text-amber-500" />}
          <span className="font-semibold text-[#0A0F1C]">{isReal ? "Real activity" : "Test / synthetic activity"}</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">
            {isReal ? "Genuine accounts (operators + self-registered)" : "Seeded / automated-test data (QA only — not traction)"}
          </span>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 self-start" role="group">
          <button data-testid="reality-toggle-real" onClick={() => setMode("real")}
            className={`px-3 py-1 text-xs font-semibold rounded ${isReal ? "bg-[#16A34A] text-white" : "text-slate-600 hover:text-[#16A34A]"}`}>Real</button>
          <button data-testid="reality-toggle-test" onClick={() => setMode("test")}
            className={`px-3 py-1 text-xs font-semibold rounded ${!isReal ? "bg-amber-500 text-white" : "text-slate-600 hover:text-amber-600"}`}>Test</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-kpis">
        <KPI icon={DollarSign} label={`MRR (${isReal ? "real" : "test"})`} value={`$${b?.mrr ?? "—"}`} sub={`ARR $${b?.arr ?? "—"}`} accent="text-[#16A34A]" />
        <KPI icon={Users} label={`Users (${isReal ? "real" : "test"})`} value={b?.users ?? "—"} sub={`${b?.paid_subscriptions ?? 0} paid`} accent="text-[#2563EB]" />
        <KPI icon={TrendingUp} label={`Revenue (${isReal ? "real" : "test"})`} value={`$${b?.total_revenue ?? "—"}`} sub={`${b?.leads ?? 0} leads · ${b?.invitations ?? 0} invites`} accent="text-[#65A30D]" />
        <KPI icon={ShieldCheck} label={`Verified (${isReal ? "real" : "test"})`} value={b?.verified_businesses ?? "—"} sub={`${b?.profiles ?? 0} profiles`} accent="text-[#C2410C]" />
      </div>

      {dr && (
        <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-xs text-slate-600" data-testid="reality-breakdown">
          <span className="font-semibold text-[#0A0F1C]">Accounts by origin:</span>{" "}
          {Object.entries(dr.by_account_origin).map(([k, v]) => (
            <span key={k} className="inline-block mr-3">{k.replace(/_/g, " ")}: <b>{v}</b></span>
          ))}
          <span className="inline-block mr-3">unclassified (legacy, not counted as real): <b>{dr.unclassified_users}</b></span>
          <span className="block mt-1 text-slate-400">Combined all-data totals (incl. test): {stats.total_users} users · ${stats.total_revenue} revenue. Charts below show combined data.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 border border-slate-200 bg-white rounded-lg p-5">
          <h3 className="font-heading font-bold text-[#0A0F1C] mb-4">Revenue over time</h3>
          {revenue && revenue.series.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={revenue.series}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} />
                <Tooltip />
                <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">No revenue data yet</div>}
        </div>
        <div className="border border-slate-200 bg-white rounded-lg p-5">
          <h3 className="font-heading font-bold text-[#0A0F1C] mb-4">Active plans</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={planData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {planData.map((e) => <Cell key={e.name} fill={PLAN_COLORS[e.name] || "#94A3B8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {health && (
        <div className="border border-slate-200 bg-white rounded-lg p-5">
          <h3 className="font-heading font-bold text-[#0A0F1C] mb-4 flex items-center gap-2"><Activity size={18} /> Platform health</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Users} label="New (24h)" value={health.new_users_24h} accent="text-[#2563EB]" />
            <KPI icon={Users} label="New (7d)" value={health.new_users_7d} accent="text-[#2563EB]" />
            <KPI icon={Ban} label="Suspended" value={health.suspended_users} accent="text-amber-500" />
            <KPI icon={Ban} label="Banned" value={health.banned_users} accent="text-red-500" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(health.services).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-green-50 text-green-700">
                <span className="h-2 w-2 rounded-full bg-green-500" /> {k.replace("_", " ")}: {v}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
